import { Link, useParams } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import Card from '../../components/base/Card';
import { useLanguage } from '../../localization';
import { type NewsItem, formatDate, itemDate, newsTagLabel } from './shared';

type DetailState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'notfound' }
  | { status: 'ready'; item: NewsItem };

/**
 * NewsDetailPage — renders a single KF1 news post fetched from
 * `GET /api/news/:id`. Shows the title, date, tag badge, full body (line breaks
 * preserved), a "Back to news" link, and — when present — a secondary external
 * source link. Handles loading and 404 states. Mirrors the standalone-page
 * chrome (Navigation + Footer) used elsewhere.
 *
 * @returns {JSX.Element} The news detail page
 */
export default function NewsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { apiPath, t } = useLanguage();
  const [state, setState] = useState<DetailState>({ status: 'loading' });

  useEffect(() => {
    if (!id) {
      setState({ status: 'notfound' });
      return;
    }

    const controller = new AbortController();
    setState({ status: 'loading' });

    async function load() {
      try {
        const res = await fetch(apiPath(`/api/news/${encodeURIComponent(id!)}`), {
          signal: controller.signal,
        });
        if (res.status === 404) {
          setState({ status: 'notfound' });
          return;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data: unknown = await res.json();
        // Accept either a bare item or `{ news: {...} }` / `{ item: {...} }`.
        const item =
          (data as { news?: NewsItem })?.news ??
          (data as { item?: NewsItem })?.item ??
          (data as NewsItem);
        if (!item || typeof item.title !== 'string') {
          setState({ status: 'notfound' });
          return;
        }
        setState({ status: 'ready', item });
      } catch (err) {
        if ((err as Error).name === 'AbortError') return;
        setState({ status: 'error' });
      }
    }

    void load();
    return () => controller.abort();
  }, [apiPath, id]);

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <Navigation />
      <main className="relative z-10 pt-20 pb-16 flex-1">
        <div className="container mx-auto px-4">
          <Link
            to="/news"
            className="mb-8 inline-block text-sm text-gray-400 transition-colors hover:text-red-400"
          >
            <i className="ri-arrow-left-s-line"></i> {t('Back to news')}
          </Link>

          {state.status === 'loading' && <DetailSkeleton />}
          {state.status === 'error' && <DetailError />}
          {state.status === 'notfound' && <DetailNotFound />}
          {state.status === 'ready' && <Article item={state.item} />}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Article({ item }: { item: NewsItem }) {
  const { t } = useLanguage();
  const date = formatDate(itemDate(item));
  return (
    <article className="mx-auto max-w-3xl">
      <header className="mb-8">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          {item.pinned && (
            <span className="inline-flex items-center gap-1 rounded-full border border-red-700/60 bg-red-950/60 px-3 py-1 text-xs font-bold uppercase tracking-wider text-red-300">
              <i className="ri-star-fill text-[10px]"></i>
              {t('Pinned')}
            </span>
          )}
          {item.tag && (
            <span className="inline-flex items-center rounded-full border border-red-900/40 bg-red-900/30 px-3 py-1 text-xs font-bold uppercase tracking-wider text-red-400">
              {t(newsTagLabel(item.tag))}
            </span>
          )}
          {date && (
            <time dateTime={itemDate(item)} className="text-sm font-medium text-gray-400">
              {date}
            </time>
          )}
        </div>
        <h1 className="font-orbitron text-3xl font-bold leading-tight horror-heading md:text-4xl">
          {item.title}
        </h1>
        <div className="mt-5 h-1 w-24 bg-red-600"></div>
      </header>

      <Card variant="dark">
        {item.excerpt && (
          <p className="mb-6 text-lg leading-relaxed text-gray-200">{item.excerpt}</p>
        )}
        {item.body ? (
          <div className="whitespace-pre-line leading-relaxed text-gray-300">{item.body}</div>
        ) : (
          !item.excerpt && <p className="text-gray-400">{t('No further details for this post.')}</p>
        )}

        {item.link && (
          <a
            href={item.link}
            target="_blank"
            rel="noopener noreferrer"
            className="group mt-8 inline-flex items-center gap-2 rounded-lg border border-red-800/60 bg-red-950/30 px-5 py-2.5 text-sm font-bold text-red-300 transition-colors hover:bg-red-900/40 hover:text-red-200"
          >
            <i className="ri-external-link-line"></i>
            {t('Source / read more')}
            <i className="ri-arrow-right-s-line transition-transform group-hover:translate-x-0.5"></i>
          </a>
        )}
      </Card>
    </article>
  );
}

function DetailSkeleton() {
  return (
    <div className="mx-auto max-w-3xl animate-pulse">
      <div className="mb-4 flex gap-3">
        <div className="h-5 w-16 rounded-full bg-gray-800"></div>
        <div className="h-5 w-24 rounded bg-gray-800"></div>
      </div>
      <div className="mb-3 h-8 w-3/4 rounded bg-gray-800"></div>
      <div className="mb-8 h-1 w-24 bg-gray-800"></div>
      <Card variant="dark">
        <div className="space-y-3">
          <div className="h-3 w-full rounded bg-gray-800"></div>
          <div className="h-3 w-11/12 rounded bg-gray-800"></div>
          <div className="h-3 w-10/12 rounded bg-gray-800"></div>
          <div className="h-3 w-2/3 rounded bg-gray-800"></div>
        </div>
      </Card>
    </div>
  );
}

function DetailNotFound() {
  return (
    <Card variant="dark" className="mx-auto max-w-2xl border-dashed text-center">
      <i className="ri-file-search-line mb-3 block text-4xl text-gray-500"></i>
      <p className="font-orbitron text-lg font-semibold text-gray-200">Post not found</p>
      <p className="mt-1 text-sm text-gray-400">
        This update may have been removed or never existed.
      </p>
      <Link
        to="/news"
        className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-red-400 transition-colors hover:text-red-300"
      >
        <i className="ri-arrow-left-s-line"></i> Back to all news
      </Link>
    </Card>
  );
}

function DetailError() {
  return (
    <Card variant="blood" className="mx-auto max-w-2xl text-center">
      <i className="ri-error-warning-line mb-3 block text-4xl text-red-400"></i>
      <p className="font-orbitron text-lg font-semibold text-white">Couldn't load this post</p>
      <p className="mt-1 text-sm text-gray-300">Please try again in a little while.</p>
    </Card>
  );
}
